import React, { useState, useMemo } from 'react';
import { CATEGORIES, REQUEST_TYPES, LOCATIONS } from '../data/beraniRequests';

const LOGO_BERANI = 'https://customer-assets-jai6qajn.emergentagent.net/job_vsepro-wanzl/artifacts/dtk3lr4t_logo%20berani.png';
const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES = 5;
const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf'];
const ACCEPT_ATTR = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf,.heic,.heif';

const Ico = ({ children, className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);
const ChevronRight = (p) => <Ico {...p}><path d="M9 18l6-6-6-6" /></Ico>;
const Check = (p) => <Ico {...p}><path d="M5 12l5 5L20 7" /></Ico>;
const AlertIcon = (p) => <Ico {...p}><path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></Ico>;
const PaperClip = (p) => <Ico {...p}><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" /></Ico>;
const XIco = (p) => <Ico {...p}><path d="M18 6L6 18M6 6l12 12" /></Ico>;

const inputCls = (hasError) =>
  `mt-2 w-full px-4 py-3 rounded-lg bg-slate-900/70 border ${
    hasError ? 'border-rose-500' : 'border-slate-700'
  } text-white placeholder:text-slate-600 focus:border-[#FCD34D] focus:outline-none transition-colors`;

const Field = ({ label, error, children }) => (
  <div className="mt-4">
    <label className="text-[11px] tracking-widest text-slate-400 font-semibold uppercase">{label}</label>
    {children}
    {error && <div className="mt-1 text-xs text-rose-400">{error}</div>}
  </div>
);

const initialForm = {
  request_type: '',
  event_date: '',
  event_time: '',
  location: '',
  location_custom: '',
  number_of_people: '',
  event_name: '',
  description: '',
  note: '',
  requester_name: '',
  requester_role: '',
  requester_phone: '',
  requester_email: '',
};

const BeraniRequestsPage = () => {
  const [step, setStep] = useState(1); // 1 category, 2 form, 3 done
  const [category, setCategory] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [attachments, setAttachments] = useState([]); // [{storage_path, filename, content_type, size, url}]
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const types = category ? REQUEST_TYPES[category.id] || [] : [];
  const locs = category ? LOCATIONS[category.id] || [] : [];

  const pickCategory = (c) => {
    setCategory(c);
    setForm({ ...initialForm, request_type: (REQUEST_TYPES[c.id] || [])[0] || '' });
    setErrors({});
    setAttachments([]);
    setUploadError('');
    setStep(2);
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFiles = async (e) => {
    setUploadError('');
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      setUploadError(`Maximálně ${MAX_FILES} souborů na požadavek.`);
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        if (!ACCEPTED_MIME.includes(f.type)) {
          setUploadError(`Soubor "${f.name}" má nepodporovaný typ. Povolené: JPG, PNG, WEBP, HEIC, PDF.`);
          continue;
        }
        if (f.size > MAX_FILE_SIZE) {
          setUploadError(`Soubor "${f.name}" je větší než 10 MB.`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const r = await fetch(`${API}/berani/requests/upload`, { method: 'POST', body: fd });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          setUploadError(`Nahrání "${f.name}" selhalo: ${err.detail || r.statusText}`);
          continue;
        }
        const meta = await r.json();
        setAttachments((prev) => [...prev, meta]);
      }
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    const e = {};
    if (!form.request_type) e.request_type = 'Vyberte typ požadavku';
    if (!form.description.trim() || form.description.trim().length < 3)
      e.description = 'Popište prosím požadavek (min. 3 znaky)';
    if (!form.requester_name.trim()) e.requester_name = 'Zadejte jméno';
    if (!form.requester_role.trim()) e.requester_role = 'Zadejte vaši funkci';
    if (!/^\S+@\S+\.\S+$/.test(form.requester_email.trim())) e.requester_email = 'Zadejte platný e-mail';
    if (!form.requester_phone.trim() || form.requester_phone.trim().length < 4)
      e.requester_phone = 'Zadejte telefon';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const finalLocation = form.location === 'Jiný' ? form.location_custom : form.location;
      const payload = {
        category: category.id,
        request_type: form.request_type,
        event_date: form.event_date || null,
        event_time: form.event_time || null,
        location: finalLocation || null,
        number_of_people: form.number_of_people ? parseInt(form.number_of_people, 10) : null,
        event_name: form.event_name || null,
        description: form.description.trim(),
        note: form.note.trim() || null,
        requester_name: form.requester_name.trim(),
        requester_role: form.requester_role.trim(),
        requester_phone: form.requester_phone.trim(),
        requester_email: form.requester_email.trim(),
        attachments,
      };
      const res = await fetch(`${API}/berani/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Chyba při odeslání');
      setResult(data.request);
      setStep(3);
      window.scrollTo(0, 0);
    } catch (e) {
      alert('Chyba: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const goBack = () => {
    setStep(1);
    setCategory(null);
    setForm(initialForm);
    setErrors({});
    setResult(null);
    setAttachments([]);
    setUploadError('');
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-slate-100" data-testid="berani-requests-page">
      <Hero />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {step === 1 && <Step1Categories onPick={pickCategory} />}
        {step === 2 && category && (
          <Step2Form
            category={category}
            types={types}
            locs={locs}
            form={form}
            upd={upd}
            errors={errors}
            submitting={submitting}
            onBack={goBack}
            onSubmit={submit}
            attachments={attachments}
            uploading={uploading}
            uploadError={uploadError}
            onFiles={handleFiles}
            onRemoveAttachment={removeAttachment}
          />
        )}
        {step === 3 && result && <Step3Done result={result} onBack={goBack} />}
      </div>
      <Footer />
    </div>
  );
};

const Hero = () => (
  <header className="relative overflow-hidden">
    <div className="absolute top-0 left-0 right-0 h-1 flex">
      <div className="flex-1 bg-[#FCD34D]" />
      <div className="flex-1 bg-[#1e3a8a]" />
      <div className="flex-1 bg-[#FCD34D]" />
    </div>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-8 sm:pt-16 sm:pb-10">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-5 min-w-0">
        <div className="relative shrink-0">
          <div aria-hidden className="absolute inset-0 rounded-full bg-white/55 blur-xl scale-[1.45] sm:bg-white/30 sm:blur-2xl sm:scale-110" />
          <img src={LOGO_BERANI} alt="Berani Zlín" className="relative h-16 sm:h-24 lg:h-28 w-auto drop-shadow-2xl" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.35em] text-[#FCD34D] mb-3 font-semibold">
            RI OKNA · BERANI ZLÍN · GASTRO
          </div>
          <h1 className="font-black leading-[1.05] text-3xl sm:text-4xl lg:text-5xl text-white">
            POŽADAVKY <span className="text-[#FCD34D]">PRO GASTRO</span>
          </h1>
          <p className="mt-4 text-slate-300 text-sm sm:text-base max-w-2xl">
            Máte požadavek, připomínku nebo přání týkající se gastro provozu? Vyberte oblast, které se požadavek týká.
          </p>
        </div>
      </div>
    </div>
  </header>
);

const Step1Categories = ({ onPick }) => (
  <div>
    <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mb-3">KROK 1</div>
    <h2 className="text-2xl sm:text-3xl font-black text-white mb-8">Vyberte oblast požadavku</h2>
    <div className="grid md:grid-cols-3 gap-5">
      {CATEGORIES.map((c) => (
        <button
          key={c.id}
          data-testid={`cat-${c.id}`}
          onClick={() => onPick(c)}
          className="group text-left rounded-2xl border border-slate-800 bg-slate-900/50 hover:bg-slate-900 hover:border-[#FCD34D] p-6 transition-all shadow-lg"
        >
          <div className="flex items-start justify-between">
            <div className="text-5xl font-black" style={{ color: c.accent }}>{c.code}</div>
            <div className="w-10 h-10 rounded-full border border-slate-700 flex items-center justify-center group-hover:border-[#FCD34D] group-hover:text-[#FCD34D]">
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-6 text-xl font-black text-white leading-tight">{c.name}</div>
          <div className="mt-2 text-sm text-slate-400 leading-relaxed">{c.tagline}</div>
        </button>
      ))}
    </div>
  </div>
);

const Step2Form = ({ category, types, locs, form, upd, errors, submitting, onBack, onSubmit, attachments, uploading, uploadError, onFiles, onRemoveAttachment }) => (
  <div>
    <button data-testid="back-to-cats" onClick={onBack} className="text-sm text-slate-400 hover:text-white mb-3">← Zpět na výběr oblasti</button>
    <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mb-2">KROK 2 · {category.name}</div>
    <h2 className="text-2xl sm:text-3xl font-black text-white mb-2">Popis požadavku</h2>
    <p className="text-slate-400 mb-8 max-w-2xl">Vyplňte prosím detaily požadavku. Povinná pole jsou označena hvězdičkou.</p>

    <div className="rounded-2xl bg-slate-900/40 border border-slate-800 p-6 sm:p-8">
      {/* Type & basic */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Typ požadavku *" error={errors.request_type}>
          <select data-testid="input-type" value={form.request_type} onChange={upd('request_type')} className={inputCls(errors.request_type)}>
            {types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        {locs.length > 0 && (
          <Field label={category.id === 'bufety' ? 'Konkrétní bufet' : 'VIP prostor'}>
            <select data-testid="input-location" value={form.location} onChange={upd('location')} className={inputCls(false)}>
              <option value="">— vyberte —</option>
              {locs.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        )}
        {form.location === 'Jiný' && (
          <Field label="Upřesněte prostor">
            <input data-testid="input-location-custom" value={form.location_custom} onChange={upd('location_custom')} className={inputCls(false)} placeholder="Vlastní upřesnění" />
          </Field>
        )}
        <Field label="Datum akce / utkání">
          <input data-testid="input-date" type="date" value={form.event_date} onChange={upd('event_date')} className={inputCls(false)} />
        </Field>
        <Field label="Čas">
          <input data-testid="input-time" type="time" value={form.event_time} onChange={upd('event_time')} className={inputCls(false)} />
        </Field>
        {category.id === 'restaurace' && (
          <>
            <Field label="Počet osob">
              <input data-testid="input-people" type="number" min={0} max={5000} value={form.number_of_people} onChange={upd('number_of_people')} className={inputCls(false)} placeholder="např. 120" />
            </Field>
            <Field label="Název akce / důvod požadavku">
              <input data-testid="input-event" value={form.event_name} onChange={upd('event_name')} className={inputCls(false)} placeholder="např. Hokejový turnaj U15" />
            </Field>
          </>
        )}
      </div>

      <Field label="Popis požadavku *" error={errors.description}>
        <textarea data-testid="input-description" value={form.description} onChange={upd('description')} rows={5} className={inputCls(errors.description)} placeholder="Popište prosím podrobněji, co potřebujete..." />
      </Field>
      <Field label="Poznámka (volitelné)">
        <textarea data-testid="input-note" value={form.note} onChange={upd('note')} rows={2} className={inputCls(false)} placeholder="Doplňující informace..." />
      </Field>

      {/* Přílohy */}
      <div className="mt-6 pt-6 border-t border-slate-800">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold">PŘÍLOHY (VOLITELNÉ)</div>
            <div className="text-xs text-slate-500 mt-1">Fotografie nebo PDF, max 10 MB / soubor, max 5 souborů.</div>
          </div>
          <label
            data-testid="upload-btn"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg cursor-pointer text-sm font-semibold border transition-colors ${
              uploading || attachments.length >= MAX_FILES
                ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                : 'bg-slate-800 border-slate-700 hover:border-[#FCD34D] text-white'
            }`}
          >
            <PaperClip className="w-4 h-4" />
            {uploading ? 'Nahrávám…' : 'Přidat soubor'}
            <input
              data-testid="file-input"
              type="file"
              multiple
              accept={ACCEPT_ATTR}
              className="hidden"
              disabled={uploading || attachments.length >= MAX_FILES}
              onChange={onFiles}
            />
          </label>
        </div>
        {uploadError && (
          <div className="mb-2 flex items-start gap-2 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
            <AlertIcon className="w-4 h-4 shrink-0 mt-0.5" />
            <div>{uploadError}</div>
          </div>
        )}
        {attachments.length === 0 ? (
          <div className="text-xs text-slate-500 italic">Zatím nejsou přidány žádné přílohy.</div>
        ) : (
          <ul className="space-y-2" data-testid="attachment-list">
            {attachments.map((a, i) => (
              <li key={a.storage_path} className="flex items-center justify-between gap-3 bg-slate-900/70 border border-slate-800 rounded-lg px-3 py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center text-[#FCD34D] shrink-0">
                    <PaperClip className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{a.filename}</div>
                    <div className="text-[11px] text-slate-500">{Math.max(1, Math.round(a.size / 1024))} kB · {a.content_type}</div>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`remove-attachment-${i}`}
                  onClick={() => onRemoveAttachment(i)}
                  className="text-slate-400 hover:text-rose-400 shrink-0"
                  aria-label="Odstranit přílohu"
                >
                  <XIco className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Requester */}
      <div className="mt-8 pt-6 border-t border-slate-800">
        <div className="text-[10px] tracking-widest text-[#93C5FD] font-bold mb-2">ZADAVATEL</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Jméno a příjmení *" error={errors.requester_name}>
            <input data-testid="input-name" value={form.requester_name} onChange={upd('requester_name')} className={inputCls(errors.requester_name)} />
          </Field>
          <Field label="Funkce *" error={errors.requester_role}>
            <input data-testid="input-role" value={form.requester_role} onChange={upd('requester_role')} className={inputCls(errors.requester_role)} placeholder="např. Manažer VIP" />
          </Field>
          <Field label="E-mail *" error={errors.requester_email}>
            <input data-testid="input-email" type="email" value={form.requester_email} onChange={upd('requester_email')} className={inputCls(errors.requester_email)} />
          </Field>
          <Field label="Telefon *" error={errors.requester_phone}>
            <input data-testid="input-phone" value={form.requester_phone} onChange={upd('requester_phone')} className={inputCls(errors.requester_phone)} placeholder="+420 ..." />
          </Field>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          data-testid="submit-btn"
          onClick={onSubmit}
          disabled={submitting}
          className="inline-flex items-center gap-2 px-8 py-4 bg-[#FCD34D] hover:bg-[#FBB040] text-slate-900 font-bold tracking-wide uppercase text-sm shadow-lg shadow-[#FCD34D]/30 disabled:opacity-60"
        >
          {submitting ? 'Odesílám…' : 'Odeslat požadavek'}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
);

const Step3Done = ({ result, onBack }) => (
  <div className="max-w-2xl mx-auto text-center">
    <div className="mx-auto w-20 h-20 rounded-full bg-[#FCD34D] text-slate-900 flex items-center justify-center shadow-lg shadow-[#FCD34D]/30">
      <Check className="w-10 h-10" />
    </div>
    <div className="text-[10px] tracking-[0.4em] text-[#FCD34D] font-bold mt-6 mb-3">POŽADAVEK BYL ÚSPĚŠNĚ ODESLÁN</div>
    <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">Děkujeme za váš požadavek</h2>
    <p className="text-slate-300 leading-relaxed max-w-lg mx-auto">
      Evidujeme jej pod číslem <strong className="text-white font-mono">{result.request_number}</strong>. Požadavku se budeme věnovat co nejdříve. Pokud bude potřeba doplnit další informace, budeme vás kontaktovat.
    </p>
    <p className="text-slate-400 leading-relaxed max-w-lg mx-auto mt-3 text-sm">
      Na váš e-mail <strong className="text-white">{result.requester_email}</strong> jsme zároveň odeslali potvrzení.
    </p>
    <button
      data-testid="back-btn"
      onClick={onBack}
      className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold tracking-wide uppercase text-sm rounded-lg"
    >
      ← Zpět na požadavky
    </button>
  </div>
);

const Footer = () => (
  <footer className="border-t border-slate-800 mt-12">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 text-sm text-slate-400 flex flex-col sm:flex-row justify-between gap-3">
      <div>Gastro pro Berani Zlín zajišťuje <strong className="text-white">Vše Pro Akci s.r.o.</strong></div>
      <div>Kontakt: <a href="mailto:catering@vseproakci.cz" className="text-[#FCD34D] hover:underline">catering@vseproakci.cz</a></div>
    </div>
    <div className="h-1 flex">
      <div className="flex-1 bg-[#FCD34D]" />
      <div className="flex-1 bg-[#1e3a8a]" />
      <div className="flex-1 bg-[#FCD34D]" />
    </div>
  </footer>
);

export default BeraniRequestsPage;
